"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { Button } from "@/shared/ui/button";
import { IntroSection } from "./intro-section";
import { ScrollIndicator } from "./scroll-indicator";
import { useIntroAnimation } from "../hooks/useIntroAnimation";
import { useHeroAnimation } from "../hooks/useHeroAnimation";
import { DotLottie, DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useTranslations } from "next-intl";
import { Form, FormControl, FormField, FormItem } from "@/shared/ui/form";
import { useForm } from "react-hook-form";
import { createEmailSchema } from "@/shared/lib/validation/user-schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useBreakpoint } from "@/shared/hooks/use-breakpoint";
import { emailSendVerification } from "@/server-action/email/email-send-verification";
import { useRouter } from "next/navigation";
import { AppError, AppErrorType, createAppError } from "@/shared/lib/errors/error-types";

type EmailFormValues = {
  email: string;
};

export function IntroHero({ isLoggedIn }: { isLoggedIn: boolean }) {
  const t = useTranslations();
  const router = useRouter();

  // Factory 방식으로 스키마 생성
  const emailFormSchema = z.object({
    email: createEmailSchema(t),
  });
  const sectionRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const textBoxRef = useRef<HTMLDivElement>(null);
  const modelImgBoxRef = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<DotLottie | null>(null);
  const [isPending, startTransition] = useTransition();

  const [shouldShowIntro, setShouldShowIntro] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const { isMobile, isPc, isTablet } = useBreakpoint();

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleSubmit = (data: EmailFormValues) => {
    startTransition(async () => {
      try {
        const email = data.email;
        const result = await emailSendVerification(email, "signup");
        if (!result.ok) {
          throw new Error(result.error.message);
        }
        router.push(`/signup?step=waiting&email=${email}`);
      } catch (error: unknown) {
        const appError =
          error instanceof AppError ? error : createAppError(error instanceof Error ? error.message : String(error));

        switch (appError.type) {
          case AppErrorType.EMAIL_ALREADY_EXISTS:
            router.push("/signup-error");
            break;
          default:
            router.push("/signup-error");
            break;
        }
        console.error(appError);
      }
    });
  };

  const { startHeroAnimation } = useHeroAnimation({
    textBoxRef,
    modelImgBoxRef,
    onComplete: () => {
      // Lottie 애니메이션 먼저 시작
      if (lottieRef.current) {
        lottieRef.current.setLoop(false);

        // Lottie 완료 시 스크롤 인디케이터 표시
        lottieRef.current.addEventListener("complete", () => {
          setTimeout(() => {
            setShowScrollIndicator(true);
          }, 800);
        });

        lottieRef.current.play();
      }
    },
  });

  useIntroAnimation({
    shouldShowIntro,
    sectionRef,
    logoRef,
    onComplete: () => {
      startHeroAnimation();
    },
  });

  useEffect(() => {
    // 첫 방문 체크
    const hasVisited = sessionStorage.getItem("hasVisited");

    if (!hasVisited) {
      setShouldShowIntro(true);
      sessionStorage.setItem("hasVisited", "true");
    } else {
      // 재방문시 바로 히어로 애니메이션 (인트로 건너뛰기)
      // 헤더와 히어로 콘텐츠 바로 표시
      const header = document.querySelector("header");
      const heroContent = document.querySelector("[data-hero-content]");

      if (header) {
        (header as HTMLElement).style.opacity = "1";
      }
      if (heroContent) {
        (heroContent as HTMLElement).style.opacity = "1";
      }

      setTimeout(() => {
        startHeroAnimation();
      }, 100);
    }
  }, []);

  return (
    <>
      {shouldShowIntro && <IntroSection sectionRef={sectionRef} logoRef={logoRef} />}

      <section className="pc:bg-center relative flex h-screen w-full items-center justify-center overflow-hidden bg-[url('/images/home/main_hero_bg.webp')] bg-cover">
        <div className="absolute top-[-46px] left-[-253px] h-[469px] w-[756px] shrink-0 rounded-[756px] bg-[#18B9F380] blur-[200px]" />
        <div className="absolute bottom-[-60px] left-[481px] h-[417px] w-[396px] shrink-0 rounded-[417px] bg-[#4CE1FF80] blur-[200px]" />

        <div className="intro-hero-bg absolute top-0 left-0 h-full w-full" />
        <div
          className="pc:right-[-120px] pc:bottom-0 pc:justify-between pc:w-full pc:flex-row pc:items-start absolute flex h-full w-full max-w-[1606px] flex-col items-center opacity-0 lg:right-[200px]"
          data-hero-content
        >
          <div
            className="pc:pt-0 pc:h-full pc:px-0 pc:w-full pc:text-left tb:pt-[172.5px] z-10 flex w-full max-w-[620px] flex-col justify-center px-4 pt-[150px] text-center opacity-0"
            ref={textBoxRef}
          >
            <h1 className="display-2 tracking-[-0.03em] whitespace-pre-line text-white">{t("mn_ban_text_1")}</h1>

            <p className="heading-4-medium tb:mt-5 mt-3 text-white">{t("mn_ban_text_2")}</p>

            {isLoggedIn ? null : (
              <>
                {(isMobile || isTablet) && (
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(handleSubmit)}
                      className="tb:mt-20 mt-7.5 flex w-full flex-col items-center"
                    >
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem className="tb:max-w-105 w-full max-w-70">
                            <FormControl>
                              <input
                                maxLength={50}
                                autoComplete="off"
                                {...field}
                                placeholder={t("mn_ban_placeholder_id")}
                                className={
                                  "body-lg-medium tb:h-14 tb:rounded-[100px] h-[46px] w-full rounded-[80px] border border-white/40 bg-white/10 px-4 text-white placeholder:text-white focus:ring-0 focus:outline-none"
                                }
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      {form.formState.errors.email && (
                        <p className="body-sm-regular tb:max-w-105 mt-[6px] w-full max-w-70 text-start text-white">
                          {form.formState.errors.email.message}
                        </p>
                      )}
                      <Button
                        type="submit"
                        className="tb:max-w-106 mt-2 w-full max-w-70"
                        variant="tertiary"
                        size={isMobile ? "md" : "lg"}
                        disabled={isPending}
                      >
                        {t("mn_ban_btn")}
                      </Button>
                    </form>
                  </Form>
                )}

                {isPc && (
                  <>
                    <div className="relative mt-20 max-w-[480px]">
                      <Form {...form}>
                        <form onSubmit={form.handleSubmit(handleSubmit)}>
                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <input
                                    maxLength={50}
                                    autoComplete="off"
                                    {...field}
                                    placeholder={t("mn_ban_placeholder_id")}
                                    className={
                                      "body-lg-medium h-[54px] w-full rounded-[80px] border border-white/40 bg-white/10 pr-[106px] pl-8 text-white placeholder:text-white focus:ring-0 focus:outline-none"
                                    }
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <Button
                            type="submit"
                            className="absolute top-1/2 right-[6px] -translate-y-1/2"
                            variant="tertiary"
                            size="md"
                          >
                            {t("mn_ban_btn")}
                          </Button>
                        </form>
                      </Form>
                    </div>
                    {form.formState.errors.email && (
                      <p className="body-sm-regular mt-2 text-white">{form.formState.errors.email.message}</p>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="pc:relative pc:self-end absolute bottom-0 shrink-0 opacity-0" ref={modelImgBoxRef}>
            <img
              src="/images/home/main_vusual_model.webp"
              alt="model"
              className="pc:h-[911px] pc:w-[940px] pc:aspect-[940/911] tb:h-[775px] tb:w-[800px] tb:aspect-[800/775] h-[349px] w-[360px]"
            />
            <DotLottieReact
              src="/lottie/dot.json"
              className="absolute top-0 left-0 aspect-[940/911] h-full w-full"
              dotLottieRefCallback={(dotLottie) => {
                lottieRef.current = dotLottie;
              }}
              renderConfig={{
                freezeOnOffscreen: false,
              }}
            />
          </div>
        </div>

        <ScrollIndicator show={showScrollIndicator} onHide={() => setShowScrollIndicator(false)} />
      </section>
    </>
  );
}
